import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton,
  MenuItem, Radio, RadioGroup, TextField, Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import BoutonAide from "../../components/charte/BoutonAide";
import { BoutonPrincipal, BoutonSecondaire } from "../../components/charte/Boutons";
import { AIDE_FACTURATION } from "../../aide/textesAide";
import { STYLE_TON } from "../../theme/tribeTheme";
import type { TonStatut } from "../../utilities/statutsQuete";
import { messageServeur } from "../../utilities/userService";
import {
  ClientFacture, DocumentEmis, EtatPaiement, LIBELLE_ETAT, MODES, ModeEncaissement, VueClient,
  annulerEncaissement, creerClient, emettreAvoir, encaisser, modifierClient, nouvelleCle, xof,
} from "../../utilities/facturationService";

/* ── Pastilles ── */

const TON_ETAT: Record<EtatPaiement, TonStatut> = {
  NON_PAYEE: "attente", PARTIELLEMENT_PAYEE: "accent", PAYEE: "succes", TROP_PERCUE: "erreur", ANNULEE: "neutre",
};

export function Pastille({ libelle, ton }: { libelle: string; ton: TonStatut }) {
  const s = STYLE_TON[ton];
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px", background: s.bg,
      border: `1px solid ${s.border}`, borderRadius: "5px", px: "8px", py: "2px", whiteSpace: "nowrap" }}>
      <Box component="span" sx={{ width: 5, height: 5, borderRadius: "50%", background: s.color }} />
      <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: s.color }}>{libelle}</Typography>
    </Box>
  );
}

export function PastilleEtat({ etat }: { etat?: EtatPaiement | null }) {
  if (!etat) return null;
  return <Pastille libelle={LIBELLE_ETAT[etat] ?? etat} ton={TON_ETAT[etat] ?? "neutre"} />;
}

export function Libelle({ children, aide }: { children: React.ReactNode; aide?: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--c-text-secondary)" }}>{children}</Typography>
      {aide && <BoutonAide texte={aide} />}
    </Box>
  );
}

function Erreur({ message }: { message: string | null }) {
  if (!message) return null;
  return <Typography sx={{ fontSize: 13, color: STYLE_TON.erreur.color, mt: 1.5, whiteSpace: "pre-wrap" }}>{message}</Typography>;
}

/* ── Éditeur de lignes : le serveur recalcule tout, l'aperçu n'est qu'indicatif ── */

export interface LigneSaisie { libelle: string; quantite: string; prixUnitaireHt: string; tauxTva: string }

export function EditeurLignes({ lignes, onChange, tauxParDefaut = "18" }: {
  lignes: LigneSaisie[]; onChange: (l: LigneSaisie[]) => void; tauxParDefaut?: string;
}) {
  const maj = (i: number, champ: keyof LigneSaisie, v: string) =>
    onChange(lignes.map((l, j) => (j === i ? { ...l, [champ]: v } : l)));
  const apercu = useMemo(() => lignes.reduce((acc, l) => {
    const ht = Math.round((parseFloat(l.quantite.replace(",", ".")) || 0) * (parseInt(l.prixUnitaireHt, 10) || 0));
    const tva = Math.round(ht * ((parseFloat(l.tauxTva.replace(",", ".")) || 0) / 100));
    return { ht: acc.ht + ht, tva: acc.tva + tva };
  }, { ht: 0, tva: 0 }), [lignes]);
  return (
    <Box>
      {lignes.map((l, i) => (
        <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField fullWidth size="small" label="Libellé" value={l.libelle} onChange={(e) => maj(i, "libelle", e.target.value)} />
          </Grid>
          <Grid item xs={4} md={2}>
            <TextField fullWidth size="small" label="Quantité" value={l.quantite} onChange={(e) => maj(i, "quantite", e.target.value)} />
          </Grid>
          <Grid item xs={4} md={2}>
            <TextField fullWidth size="small" label="PU HT (XOF)" value={l.prixUnitaireHt} onChange={(e) => maj(i, "prixUnitaireHt", e.target.value)} />
          </Grid>
          <Grid item xs={3} md={2}>
            <TextField fullWidth size="small" label="TVA %" value={l.tauxTva} onChange={(e) => maj(i, "tauxTva", e.target.value)} />
          </Grid>
          <Grid item xs={1}>
            <IconButton aria-label="Retirer la ligne" disabled={lignes.length === 1}
              onClick={() => onChange(lignes.filter((_, j) => j !== i))}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
          </Grid>
        </Grid>
      ))}
      <BoutonSecondaire size="small" onClick={() => onChange([...lignes, { libelle: "", quantite: "1", prixUnitaireHt: "", tauxTva: tauxParDefaut }])}>
        Ajouter une ligne
      </BoutonSecondaire>
      <Typography sx={{ fontSize: 12.5, color: "var(--c-text-secondary)", mt: 1 }}>
        Aperçu : {xof(apercu.ht)} HT · {xof(apercu.tva)} TVA · {xof(apercu.ht + apercu.tva)} TTC (le serveur fait foi).
      </Typography>
    </Box>
  );
}

/** Lignes saisies → corps attendu par le serveur (taux en fraction). */
export function lignesPourServeur(lignes: LigneSaisie[]) {
  return lignes.map((l) => ({
    libelle: l.libelle.trim(),
    quantite: l.quantite.replace(",", ".").trim(),
    prixUnitaireHt: l.prixUnitaireHt.replace(/\s/g, "").trim(),
    tauxTva: l.tauxTva.trim() === "" ? undefined : String((parseFloat(l.tauxTva.replace(",", ".")) || 0) / 100),
  }));
}

/* ── Fiche client ── */

const CHAMPS_FISCAUX: Record<string, { key: keyof ClientFacture; label: string }[]> = {
  SN: [{ key: "ninea", label: "NINEA" }, { key: "rccm", label: "RCCM" }],
  CI: [{ key: "ncc", label: "NCC" }, { key: "rccm", label: "RCCM" }],
};

export function DialogClient({ ouvert, vue, onFerme, onEnregistre }: {
  ouvert: boolean; vue: VueClient | null; onFerme: () => void; onEnregistre: (v: VueClient) => void;
}) {
  const [c, setC] = useState<ClientFacture>({ pays: "SN" });
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => { if (ouvert) { setC(vue?.client ?? { pays: "SN" }); setErreur(null); } }, [ouvert, vue]);
  const champ = (key: keyof ClientFacture, label: string, md = 6) => (
    <Grid item xs={12} md={md}>
      <TextField fullWidth size="small" label={label} value={(c[key] as string) ?? ""}
        onChange={(e) => setC({ ...c, [key]: e.target.value })} />
    </Grid>
  );
  const pays = (c.pays ?? "").toUpperCase();
  const enregistrer = async () => {
    setEnvoi(true); setErreur(null);
    try {
      const v = c.id ? await modifierClient(c.id, c) : await creerClient(c);
      onEnregistre(v);
    } catch (e: any) {
      setErreur(messageServeur(e, "Enregistrement refusé."));
    } finally { setEnvoi(false); }
  };
  return (
    <Dialog open={ouvert} onClose={onFerme} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {c.id ? "Modifier le client" : "Nouveau client facturé"} <BoutonAide texte={AIDE_FACTURATION.clients} />
      </DialogTitle>
      <DialogContent>
        {vue && !vue.complet && vue.manquants.length > 0 && (
          <Typography sx={{ fontSize: 13, color: STYLE_TON.erreur.color, mb: 1.5 }}>
            Manque pour émettre : {vue.manquants.join(", ")}.
          </Typography>
        )}
        <Libelle aide={AIDE_FACTURATION.identite}>Identité</Libelle>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {champ("raisonSociale", "Raison sociale")}
          <Grid item xs={12} md={pays === "SN" || pays === "CI" ? 6 : 3}>
            <TextField select fullWidth size="small" label="Pays" value={pays === "SN" || pays === "CI" ? pays : "AUTRE"}
              onChange={(e) => setC({ ...c, pays: e.target.value === "AUTRE" ? "" : e.target.value })}>
              <MenuItem value="SN">Sénégal</MenuItem>
              <MenuItem value="CI">Côte d'Ivoire</MenuItem>
              <MenuItem value="AUTRE">Autre pays</MenuItem>
            </TextField>
          </Grid>
          {pays !== "SN" && pays !== "CI" && (
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Code pays (ISO, ex. FR)" value={c.pays ?? ""}
                inputProps={{ maxLength: 2 }} onChange={(e) => setC({ ...c, pays: e.target.value.toUpperCase() })} />
            </Grid>
          )}
          {champ("adresse", "Adresse", 12)}
        </Grid>
        <Libelle aide={AIDE_FACTURATION.identifiantsFiscaux}>Identifiants fiscaux</Libelle>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {(CHAMPS_FISCAUX[pays] ?? [{ key: "identifiantFiscal" as keyof ClientFacture, label: "Identifiant fiscal du pays" }])
            .map((f) => <React.Fragment key={f.key}>{champ(f.key, f.label)}</React.Fragment>)}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TextField fullWidth size="small" label="Numéro de TVA" value={c.numeroTva ?? ""}
                onChange={(e) => setC({ ...c, numeroTva: e.target.value })} />
              <BoutonAide texte={AIDE_FACTURATION.numeroTva} />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TextField fullWidth size="small" label="Motif d'exonération de TVA (vide : non exonéré)"
                value={c.motifExonerationTva ?? ""} onChange={(e) => setC({ ...c, motifExonerationTva: e.target.value })} />
              <BoutonAide texte={AIDE_FACTURATION.exoneration} />
            </Box>
          </Grid>
        </Grid>
        <Libelle aide={AIDE_FACTURATION.contact}>Contact</Libelle>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {champ("contactNom", "Nom du contact", 4)}
          {champ("contactEmail", "Email du contact", 4)}
          {champ("contactTelephone", "Téléphone", 4)}
        </Grid>
        <Libelle aide={AIDE_FACTURATION.comptePortail}>Compte du portail client</Libelle>
        <Grid container spacing={1.5}>
          {champ("emailPortail", "Email de connexion au portail (vide : email du contact)", 12)}
        </Grid>
        <Erreur message={erreur} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <BoutonSecondaire onClick={onFerme}>Annuler</BoutonSecondaire>
        <BoutonPrincipal disabled={envoi || !c.raisonSociale?.trim()} onClick={enregistrer}>Enregistrer</BoutonPrincipal>
      </DialogActions>
    </Dialog>
  );
}

/* ── Encaissement : la case trop-perçu n'apparaît qu'après un refus du serveur ── */

const aujourdhui = () => new Date().toISOString().slice(0, 10);

export function DialogEncaissement({ facture, ouvert, onFerme, onEnregistre }: {
  facture: { id: number; numero: string; resteDuTtc?: number | null } | null; ouvert: boolean;
  onFerme: () => void; onEnregistre: () => void;
}) {
  const [montant, setMontant] = useState("");
  const [dateValeur, setDateValeur] = useState(aujourdhui());
  const [mode, setMode] = useState<ModeEncaissement>("VIREMENT");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [refusDepassement, setRefusDepassement] = useState(false);
  const [tropPercu, setTropPercu] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [cle, setCle] = useState("");
  useEffect(() => {
    if (!ouvert) return;
    setMontant(facture?.resteDuTtc && facture.resteDuTtc > 0 ? String(facture.resteDuTtc) : "");
    setDateValeur(aujourdhui()); setMode("VIREMENT"); setReference(""); setNote("");
    setRefusDepassement(false); setTropPercu(false); setErreur(null); setCle(nouvelleCle("encaissement"));
  }, [ouvert, facture]);
  if (!facture) return null;
  const enregistrer = async () => {
    setEnvoi(true); setErreur(null);
    try {
      await encaisser(facture.id, {
        montant: parseInt(montant.replace(/\s/g, ""), 10), dateValeur, mode,
        referenceExterne: reference.trim() || undefined, note: note.trim() || undefined,
        tropPercu: refusDepassement && tropPercu ? true : undefined, cle,
      });
      onEnregistre();
    } catch (e: any) {
      const msg = messageServeur(e, "Encaissement refusé.");
      if (e?.response?.status === 409 && msg.includes("tropPercu")) setRefusDepassement(true);
      setErreur(msg);
    } finally { setEnvoi(false); }
  };
  return (
    <Dialog open={ouvert} onClose={onFerme} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Encaissement sur {facture.numero} <BoutonAide texte={AIDE_FACTURATION.encaissement} />
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "var(--c-text-secondary)", mb: 2 }}>Reste dû : {xof(facture.resteDuTtc)}</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6}><TextField fullWidth size="small" label="Montant (XOF)" value={montant} onChange={(e) => setMontant(e.target.value)} /></Grid>
          <Grid item xs={6}><TextField fullWidth size="small" type="date" label="Date de valeur" InputLabelProps={{ shrink: true }}
            value={dateValeur} onChange={(e) => setDateValeur(e.target.value)} /></Grid>
          <Grid item xs={6}>
            <TextField select fullWidth size="small" label="Mode" value={mode} onChange={(e) => setMode(e.target.value as ModeEncaissement)}>
              {MODES.map((m) => <MenuItem key={m.valeur} value={m.valeur}>{m.libelle}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}><TextField fullWidth size="small" label="Référence de la transaction" value={reference} onChange={(e) => setReference(e.target.value)} /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" label="Note (facultative)" value={note} onChange={(e) => setNote(e.target.value)} /></Grid>
        </Grid>
        {refusDepassement && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5 }}>
            <FormControlLabel label="Enregistrer comme trop-perçu" control={<Checkbox checked={tropPercu} onChange={(e) => setTropPercu(e.target.checked)} />} />
            <BoutonAide texte={AIDE_FACTURATION.tropPercu} />
          </Box>
        )}
        <Erreur message={erreur} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <BoutonSecondaire onClick={onFerme}>Annuler</BoutonSecondaire>
        <BoutonPrincipal disabled={envoi || !montant.trim() || !dateValeur} onClick={enregistrer}>Enregistrer l'encaissement</BoutonPrincipal>
      </DialogActions>
    </Dialog>
  );
}

export function DialogAnnulation({ encaissement, onFerme, onEnregistre }: {
  encaissement: { id: number; montant: number; factureNumero: string } | null; onFerme: () => void; onEnregistre: () => void;
}) {
  const [motif, setMotif] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [cle, setCle] = useState("");
  useEffect(() => { if (encaissement) { setMotif(""); setErreur(null); setCle(nouvelleCle("annulation")); } }, [encaissement]);
  if (!encaissement) return null;
  const valider = async () => {
    try {
      await annulerEncaissement(encaissement.id, motif.trim(), cle);
      onEnregistre();
    } catch (e: any) { setErreur(messageServeur(e, "Annulation refusée.")); }
  };
  return (
    <Dialog open onClose={onFerme} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Annuler l'encaissement de {xof(encaissement.montant)} <BoutonAide texte={AIDE_FACTURATION.annulation} />
      </DialogTitle>
      <DialogContent>
        <TextField fullWidth size="small" label="Motif (obligatoire)" value={motif} onChange={(e) => setMotif(e.target.value)} sx={{ mt: 1 }} />
        <Erreur message={erreur} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <BoutonSecondaire onClick={onFerme}>Fermer</BoutonSecondaire>
        <BoutonPrincipal disabled={!motif.trim()} onClick={valider}>Annuler l'encaissement</BoutonPrincipal>
      </DialogActions>
    </Dialog>
  );
}

/* ── Avoir total ou partiel ── */

export function DialogAvoir({ facture, onFerme, onEmis }: {
  facture: DocumentEmis | null; onFerme: () => void; onEmis: (a: DocumentEmis) => void;
}) {
  const [mode, setMode] = useState<"total" | "partiel">("total");
  const [motif, setMotif] = useState("");
  const [lignes, setLignes] = useState<LigneSaisie[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [cle, setCle] = useState("");
  const taux = facture?.lignes?.[0] ? String(Math.round(parseFloat(String(facture.lignes[0].tauxTva)) * 10000) / 100) : "18";
  useEffect(() => {
    if (!facture) return;
    setMode("total"); setMotif(""); setErreur(null); setCle(nouvelleCle("avoir"));
    setLignes([{ libelle: "", quantite: "1", prixUnitaireHt: "", tauxTva: taux }]);
  }, [facture, taux]);
  if (!facture) return null;
  const emettre = async () => {
    try {
      onEmis(await emettreAvoir(facture.id, { motif: motif.trim(), cle,
        lignes: mode === "partiel" ? lignesPourServeur(lignes) as any : undefined }));
    } catch (e: any) { setErreur(messageServeur(e, "Avoir refusé.")); }
  };
  return (
    <Dialog open onClose={onFerme} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Avoir sur la facture {facture.numero} <BoutonAide texte={AIDE_FACTURATION.avoir} />
      </DialogTitle>
      <DialogContent>
        <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value as any)}>
          <FormControlLabel value="total" control={<Radio />} label="Total : annule ce qui reste de la facture" />
          <FormControlLabel value="partiel" control={<Radio />} label="Partiel : lignes à créditer" />
        </RadioGroup>
        {mode === "partiel" && <Box sx={{ mt: 1 }}><EditeurLignes lignes={lignes} onChange={setLignes} tauxParDefaut={taux} /></Box>}
        <TextField fullWidth size="small" label="Motif (obligatoire, imprimé sur l'avoir)" value={motif}
          onChange={(e) => setMotif(e.target.value)} sx={{ mt: 2 }} />
        <Erreur message={erreur} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <BoutonSecondaire onClick={onFerme}>Annuler</BoutonSecondaire>
        <BoutonPrincipal disabled={!motif.trim()} onClick={emettre}>Émettre l'avoir</BoutonPrincipal>
      </DialogActions>
    </Dialog>
  );
}

export { Erreur };
